/* eslint-disable @typescript-eslint/explicit-function-return-type */
import axios, {
  type AxiosInstance,
  type AxiosPromise,
  type AxiosResponse,
} from 'axios';

import type { Country } from '../features';

import { DefaultDelay, retrier } from './apiRetry';
import Headers, { type AuthPolicy } from './headers';
import {
  includeAuthHeaderRequestInterceptor,
  logRequestInterceptor,
  logResponseErrorInterceptor,
} from './interceptors';

export const SupportedEnvironments = ['local', 'dev', 'prod'] as const;

export type Environment = (typeof SupportedEnvironments)[number];

/** API request configuration */
export interface ApiConfig {
  /** Environment this UI is running in  */
  environment: Environment;
  /** 2-letter country code of this website */
  country: Country;

  auth: AuthPolicy;
  /**
   * FOR TESTING PURPOSES ONLY
   *
   * If set, all api requests will use this value as the base url path.
   */
  dangerouslyUseFullUrl?: string;

  /**
   * FOR TESTING PURPOSES ONLY
   *
   * If set, all GraphQL requests will use this value as the base url path.
   */
  dangerouslyUseGraphQL?: string;
}

let baseUrl: string | null = null;
let hirerExpBaseUrl: string | null = null;
let auth: AuthPolicy | null = null;
let uniqueAxiosInstance: AxiosInstance | null = null;
let hirerExpUniqueAxiosInstance: AxiosInstance | null = null;

const apiEndpointBaseUrl = (): string => {
  if (baseUrl) {
    return baseUrl;
  }
  throw new Error('API Endpoint not configured.');
};

const getHirerExpBaseUrl = (): string => {
  if (hirerExpBaseUrl) {
    return hirerExpBaseUrl;
  }
  throw new Error('Hirer Exp API Endpoint not configured.');
};

const headerService = () => {
  if (!auth) {
    throw new Error('Missing auth, invoke api call cannot proceed');
  }

  return new Headers(auth);
};

const RR_API_BASE_URLS: Record<'local' | 'dev' | 'prod', string> = {
  local: 'http://localhost:3000',
  dev: 'https://rr-api-oidc.staging.cloud.seek.com.au',
  prod: 'https://rr-api-oidc.cloud.seek.com.au',
};

const HIRER_EXP_API_BASE_URLS: Record<'local' | 'dev' | 'prod', string> = {
  local: 'http://localhost:3005',
  dev: 'https://rr-hirer-experience-api.staging.cloud.seek.com.au',
  prod: 'https://rr-hirer-experience-api.cloud.seek.com.au',
};

const createBaseUrl = (
  config: ApiConfig,
  baseUrls: Record<'local' | 'dev' | 'prod', string>,
) => {
  if (config.dangerouslyUseFullUrl) {
    return config.dangerouslyUseFullUrl;
  }

  const { environment } = config;

  if (!SupportedEnvironments.includes(environment)) {
    throw new Error(
      `Unsupported environment value '${environment}'. Expected one of: ${SupportedEnvironments}`,
    );
  }

  const endpoint = baseUrls[environment];

  if (endpoint) {
    return endpoint;
  }

  throw new Error('Unsupported config');
};

/** Configures global SDK calls */
const configure = (config: ApiConfig): void => {
  baseUrl = createBaseUrl(config, RR_API_BASE_URLS);
  hirerExpBaseUrl = createBaseUrl(config, HIRER_EXP_API_BASE_URLS);

  if (!config.auth) {
    throw new Error('No auth config provided');
  }
  auth = config.auth;
};

const axiosInstance = (): AxiosInstance => {
  if (!uniqueAxiosInstance) {
    const instance = axios.create();
    instance.interceptors.request.use(
      includeAuthHeaderRequestInterceptor(headerService, apiEndpointBaseUrl),
    );
    instance.interceptors.request.use(logRequestInterceptor);
    instance.interceptors.response.use(
      (res) => res,
      logResponseErrorInterceptor,
    );
    uniqueAxiosInstance = instance;
  }

  return uniqueAxiosInstance;
};

const hirerExpAxiosInstance = (): AxiosInstance => {
  if (!hirerExpUniqueAxiosInstance) {
    const instance = axios.create({
      baseURL: getHirerExpBaseUrl(),
    });

    instance.interceptors.request.use(async (config) => {
      const headers = await headerService().getAuthHeaders();

      config.headers.set(headers);

      return config;
    });
    instance.interceptors.request.use(logRequestInterceptor);
    instance.interceptors.response.use(
      (res) => res,
      logResponseErrorInterceptor,
    );
    hirerExpUniqueAxiosInstance = instance;
  }

  return hirerExpUniqueAxiosInstance;
};

const invoke = <T>(
  apiToInvoke: Promise<
    (axios: AxiosInstance, basePath: string) => AxiosPromise<T>
  >,
): Promise<AxiosResponse<T>> =>
  retrier(
    async () => {
      const instance = await apiToInvoke;
      return instance(axiosInstance(), apiEndpointBaseUrl());
    },
    {
      strategy: 'fixed',
      delayMs: DefaultDelay,
    },
  );

export {
  invoke,
  configure,
  axiosInstance,
  apiEndpointBaseUrl,
  hirerExpAxiosInstance,
  getHirerExpBaseUrl,
};
